import {
  Background,
  BackgroundVariant,
  Controls,
  ReactFlow,
  type EdgeMouseHandler,
  type EdgeTypes,
  type OnEdgesChange,
  type OnNodesChange,
  type NodeMouseHandler,
  type NodeTypes,
  useReactFlow,
} from '@xyflow/react';
import { Global, Transition } from '@mantine/core';
import { Footer, GraphEditorHeader } from '../../molecules';
import { GraphNode } from '@morph-mapper/node-logic';
import { useEffect } from 'react';
import { useLayout } from './use-layout';
import { useStore } from '@/store';
import { useGraphTypes } from './use-graph-types';
import { useGraphContext } from '../../context';
import { RFGraphEdge, RFGraphNode } from './utils';
import { DrawerKind } from '../../store/ui';
import '@xyflow/react/dist/style.css';
import { useGraphViewState } from './use-graph-view-state';
import { NodeSelectDrawer } from '../node-select-drawer';
import { NodeConfigPanel, NodeConfigProvider } from '../node-config-panel';
import { GraphConfigPanel } from '../graph-config-panel';
import { match } from 'ts-pattern';
import {
  DrawerContainer,
  DrawerContent,
  GraphCanvasContainer,
  GraphViewBody,
  GraphViewGlobalStyles,
  GraphViewRoot,
  ReactFlowStyle,
} from './styles';

export const GraphView = () => {
  /** Global state */
  const variant = useStore(({ config }) => config.getVariant());
  /** Context state */
  const [openDrawer, closeDrawer, view, graph, enqueueGraph] = useGraphContext(
    ({ ui, graph: g }) => [
      ui.openDrawer,
      ui.closeDrawer,
      ui.view,
      g.get(),
      g.enqueueGraph,
    ]
  );
  /** Local state */
  const { nodes, edges, setNodes, setEdges, onNodesChange, onEdgesChange } =
    useGraphViewState(graph, view);
  /** Hooks */
  const { nodeTypes, edgeTypes } = useGraphTypes(variant);
  const { updateEdge } = useReactFlow();

  useLayout({
    layoutNodes: nodes,
    layoutEdges: edges,
    setNodes,
  });

  useEffect(() => {
    const nodeIds = Object.keys(graph.nodes ?? {});
    if (nodeIds.length === 0) return;
    enqueueGraph(nodeIds);
    // Run once when the visible graph changes; node edits enqueue themselves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph.id, enqueueGraph]);

  /** Handlers */
  const handleRunGraph = () => {
    enqueueGraph(Object.keys(graph.nodes));
  };
  const handleOpenConfig = () => {
    openDrawer({ kind: DrawerKind.GraphConfig });
  };

  const handleNodeClick: NodeMouseHandler<RFGraphNode> = (_event, node) => {
    const graphNode = graph.nodes[node.id];
    if (!graphNode || graphNode.kind === GraphNode.Terminal) {
      return;
    }

    openDrawer({ kind: DrawerKind.NodeConfig, nodeId: node.id });
  };

  const handleEdgeMouseEnter: EdgeMouseHandler = (_, edge) => {
    updateEdge(edge.id, { zIndex: 2, data: { ...edge.data, isHovered: true } });
  };

  const handleEdgeMouseLeave: EdgeMouseHandler = (_, edge) => {
    updateEdge(edge.id, { zIndex: 0, data: { ...edge.data, isHovered: false } });
  };

  const handlePaneClick = () => {
    closeDrawer();
    setNodes((current) =>
      current.map((node) => ({
        ...node,
        selected: false,
      }))
    );
    setEdges((current) =>
      current.map((edge) => ({
        ...edge,
        selected: false,
      }))
    );
  };

  return (
    <>
      <Global styles={GraphViewGlobalStyles} />
      <GraphViewRoot>
        <GraphEditorHeader
          onRun={handleRunGraph}
          onOpenConfig={handleOpenConfig}
        />
        <GraphViewBody>
          <GraphCanvas
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={handleNodeClick}
            onEdgeMouseEnter={handleEdgeMouseEnter}
            onEdgeMouseLeave={handleEdgeMouseLeave}
            onPaneClick={handlePaneClick}
          />
          <GraphDrawer />
        </GraphViewBody>
        <Footer />
      </GraphViewRoot>
    </>
  );
};

type GraphCanvasProps = {
  nodes: RFGraphNode[];
  edges: RFGraphEdge[];
  nodeTypes: NodeTypes;
  edgeTypes: EdgeTypes;
  onNodesChange: OnNodesChange<RFGraphNode>;
  onEdgesChange: OnEdgesChange<RFGraphEdge>;
  onNodeClick: NodeMouseHandler<RFGraphNode>;
  onEdgeMouseEnter: EdgeMouseHandler;
  onEdgeMouseLeave: EdgeMouseHandler;
  onPaneClick: () => void;
};

const GraphCanvas = ({
  nodes,
  edges,
  nodeTypes,
  edgeTypes,
  onNodesChange,
  onEdgesChange,
  onNodeClick,
  onEdgeMouseEnter,
  onEdgeMouseLeave,
  onPaneClick,
}: GraphCanvasProps) => {
  return (
    <GraphCanvasContainer>
      <ReactFlow
        className="graph-view"
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onEdgeMouseEnter={onEdgeMouseEnter}
        onEdgeMouseLeave={onEdgeMouseLeave}
        onPaneClick={onPaneClick}
        nodesDraggable={false}
        nodesConnectable={false}
        edgesFocusable={false}
        minZoom={0.05}
        fitView
        style={ReactFlowStyle}
      >
        <Controls position="top-right" />
        <Background variant={BackgroundVariant.Cross} gap={12} size={1} />
      </ReactFlow>
    </GraphCanvasContainer>
  );
};

const GraphDrawer = () => {
  const drawer = useGraphContext(({ ui }) => ui.drawer);

  return (
    <DrawerContainer kind={drawer.kind}>
      <Transition
        mounted={drawer.kind !== DrawerKind.None}
        transition="slide-left"
        duration={200}
        timingFunction="ease"
      >
        {(styles) => (
          <DrawerContent style={styles}>
            {match(drawer)
              .with({ kind: DrawerKind.NodePicker }, () => <NodeSelectDrawer />)
              .with({ kind: DrawerKind.NodeConfig }, ({ nodeId }) => (
                <NodeConfigProvider nodeId={nodeId}>
                  <NodeConfigPanel />
                </NodeConfigProvider>
              ))
              .with({ kind: DrawerKind.GraphConfig }, () => (
                <GraphConfigPanel />
              ))
              .otherwise(() => null)}
          </DrawerContent>
        )}
      </Transition>
    </DrawerContainer>
  );
};
